import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * 🧭 Representa la notificación completa según NotificationDTO del backend.
 */
export interface Notification {
  id: string;                // ID de la relación usuario ↔ notificación
  notificationId: string;    // ID global de la notificación base
  title: string;
  message: string;
  type: string;              // Enum: STOCK_LOW, SALE, etc.
  level: string;             // Enum: INFO, WARNING, ERROR, SUCCESS
  isRead: boolean;
  createdAt: string;         // Fecha global
  assignedAt: string;        // Fecha de asignación al usuario
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
};

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    /**
     * 🔄 Carga todas las notificaciones desde el backend.
     */
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter((n) => !n.isRead).length;
    },

    /**
     * 📨 Agrega una nueva notificación al inicio de la lista.
     */
    addNotification: (state, action: PayloadAction<Notification>) => {
      const exists = state.notifications.some(
        (n) => n.id === action.payload.id
      );
      if (!exists) {
        state.notifications.unshift(action.payload);
        if (!action.payload.isRead) state.unreadCount += 1;
      }
    },

    /**
     * ✅ Marca una notificación como leída.
     */
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload
      );
      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount = Math.max(state.unreadCount - 1, 0);
      }
    },

    /**
     * 🗑️ Elimina una notificación específica del estado.
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
      state.unreadCount = state.notifications.filter((n) => !n.isRead).length;
    },

    /**
     * 🧹 Limpia todas las notificaciones del usuario.
     */
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
  },
});

export const {
  setNotifications,
  addNotification,
  markAsRead,
  removeNotification,
  clearNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
